import React, { useState, useEffect, useRef } from 'react';
import { Heart, X, MapPin, Star, Clock, Flame, Zap, Gift, Trophy, Target, Filter, Shuffle, Bookmark, Share2, Eye, TrendingUp, Award, Sparkles, Volume2, VolumeX } from 'lucide-react';


export const DiscoverCard = ({ product, isTop, swipeDirection, onSwipe, cardIndex, isMobile }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);

  const detectSwipeVelocity = (startTime, endTime, distance) => {
    const duration = endTime - startTime;
    const velocity = distance / duration; // pixels per millisecond
    
    // Adjust these thresholds based on testing
    // Fast swipe = more than 0.8 pixels per millisecond
    return velocity > 0.8 ? 'hard' : 'normal';
  };
  
  const handleTouchStart = (e) => {
    if (!isTop) return;
    setIsDragging(true);
    const touch = e.touches[0] || e;
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setCurrentX(touch.clientX);
    setCurrentY(touch.clientY);
    setStartTime(Date.now());
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !isTop) return;
    e.preventDefault();
    const touch = e.touches[0] || e;
    setCurrentX(touch.clientX);
    setCurrentY(touch.clientY);
  };

  const handleTouchEnd = (e) => {
    if (!isDragging || !isTop) return;
    setIsDragging(false);
    
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const endTime = Date.now();
    
    // Only trigger if horizontal swipe is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      const direction = deltaX > 0 ? 'right' : 'left';
      const velocity = detectSwipeVelocity(startTime, endTime, distance);
      onSwipe(direction, velocity);
    }
    
    // Reset position
    setCurrentX(0);
    setCurrentY(0);
  };

  // Mouse events for desktop
  const handleMouseDown = (e) => {
    if (!isTop) return;
    setIsDragging(true);
    setStartX(e.clientX);
    setStartY(e.clientY);
    setCurrentX(e.clientX);
    setCurrentY(e.clientY);
    setStartTime(Date.now());
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !isTop) return;
    setCurrentX(e.clientX);
    setCurrentY(e.clientY);
  };

  const handleMouseUp = (e) => {
    if (!isDragging || !isTop) return;
    setIsDragging(false);
    
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const endTime = Date.now();
    
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      const direction = deltaX > 0 ? 'right' : 'left';
      const velocity = detectSwipeVelocity(startTime, endTime, distance);
      onSwipe(direction, velocity);
    }
    
    setCurrentX(0);
    setCurrentY(0);
  };

  // In your swipe handler
  const handleSwipeEnd = (direction, startTime, endTime, distance) => {
    const velocity = detectSwipeVelocity(startTime, endTime, distance);
    onSwipe(direction, velocity);
  };

  const getAnimationStyle = () => {
    if (swipeDirection === "left") {
      return {
        transform: 'translateX(-120%) rotate(-30deg)',
        opacity: 0,
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }
    }
    if (swipeDirection === "right") {
      return {
        transform: 'translateX(120%) rotate(30deg)',
        opacity: 0,
        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
      }
    }
    return {}
  }

  const handleCardClick = (e) => {
    if (!isTop) return;
    e.stopPropagation();
    setShowDetails(!showDetails);
  };

  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: isMobile ? '20px' : '24px',
        overflow: 'hidden',
        transition: isDragging ? 'none' : 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isTop 
          ? `scale(1) translateY(0) translateX(${isDragging ? (currentX - startX) * 0.1 : 0}px) rotate(${isDragging ? (currentX - startX) * 0.02 : 0}deg)` 
          : `scale(${0.95 - cardIndex * 0.02}) translateY(${-cardIndex * (isMobile ? 8 : 12)}px)`,
        cursor: isTop ? 'grab' : 'default',
        zIndex: isTop ? 10 : 10 - cardIndex,
        boxShadow: isTop 
          ? isMobile 
            ? '0 15px 30px -8px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)' 
            : '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)'
          : isMobile
            ? '0 6px 15px -3px rgba(0, 0, 0, 0.2)'
            : '0 10px 25px -5px rgba(0, 0, 0, 0.2)',
        ...getAnimationStyle()
      }}
      onClick={handleCardClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp} // Handle mouse leaving the card
    >
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        {/* Background Image */}
        <img 
          src={product.image} 
          alt={product.name} 
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover',
            transition: 'transform 0.3s ease'
          }} 
        />
        
        {/* Gradient Overlays */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.8) 100%)'
        }} />

        {/* Top Status Badges */}
        <div style={{
          position: 'absolute',
          top: isMobile ? '16px' : '20px',
          left: isMobile ? '16px' : '20px',
          right: isMobile ? '16px' : '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: isMobile ? '6px' : '8px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '6px' : '8px' }}>
            {product.isNew && (
              <div style={{
                padding: isMobile ? '4px 8px' : '6px 12px',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: 'white',
                borderRadius: '20px',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backdropFilter: 'blur(10px)'
              }}>
                <Sparkles size={isMobile ? 10 : 12} />
                NEW
              </div>
            )}
            {product.isTrending && (
              <div style={{
                padding: isMobile ? '4px 8px' : '6px 12px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                borderRadius: '20px',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backdropFilter: 'blur(10px)'
              }}>
                <Flame size={isMobile ? 10 : 12} />
                TRENDING
              </div>
            )}
          </div>
          
          <div style={{
            padding: isMobile ? '6px 12px' : '8px 16px',
            background: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            borderRadius: '20px',
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '700',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {product.matchScore}% Match
          </div>
        </div>

        {/* Deal Badge */}
        {product.discount && (
          <div style={{
            position: 'absolute',
            top: isMobile ? '16px' : '20px',
            right: isMobile ? '16px' : '20px',
            transform: 'translateX(80px)',
            animation: 'slideInRight 0.8s ease-out 0.5s forwards'
          }}>
            <div style={{
              padding: isMobile ? '6px 12px' : '8px 16px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              borderRadius: '20px',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
            }}>
              <Gift size={isMobile ? 12 : 14} />
              {product.discount}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: isMobile ? '24px 16px 16px' : '32px 24px 24px',
          color: 'white'
        }}>
          {/* Product Name & Type */}
          <div style={{ marginBottom: isMobile ? '8px' : '12px' }}>
            <h3 style={{ 
              fontSize: isMobile ? '24px' : '32px', 
              fontWeight: '800', 
              margin: '0 0 4px 0',
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              lineHeight: isMobile ? '1.2' : '1.1'
            }}>
              {product.name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
              <span style={{
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                color: product.type === 'Sativa' ? '#10b981' : product.type === 'Indica' ? '#8b5cf6' : '#f59e0b',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                {product.type}
              </span>
              <span style={{ fontSize: isMobile ? '14px' : '16px', opacity: 0.9 }}>•</span>
              <span style={{ fontSize: isMobile ? '14px' : '16px', opacity: 0.9 }}>{product.category}</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '12px' : '16px',
            marginBottom: isMobile ? '12px' : '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Star size={isMobile ? 14 : 16} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
              <span style={{ fontWeight: '600', fontSize: isMobile ? '14px' : '16px' }}>{product.rating}</span>
              <span style={{ opacity: 0.8, fontSize: isMobile ? '14px' : '16px' }}>({product.reviews})</span>
            </div>
            <div style={{
              padding: isMobile ? '3px 8px' : '4px 12px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '600',
              backdropFilter: 'blur(10px)'
            }}>
              THC {product.thc}
            </div>
          </div>

          {/* Price & Dispensary */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: isMobile ? '12px' : '16px',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '8px' : '0'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  fontSize: isMobile ? '24px' : '28px', 
                  fontWeight: '800',
                  color: '#00d4aa'
                }}>
                  {product.price}
                </span>
                {product.discount && (
                  <span style={{
                    fontSize: isMobile ? '12px' : '14px',
                    opacity: 0.8,
                    textDecoration: 'line-through'
                  }}>
                    {product.priceRange.split('-')[1]}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Effects Pills */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: isMobile ? '6px' : '8px',
            marginBottom: showDetails ? (isMobile ? '12px' : '16px') : '0'
          }}>
            {product.effects.slice(0, isMobile ? 2 : 3).map((effect) => (
              <span key={effect} style={{
                padding: isMobile ? '4px 8px' : '6px 12px',
                background: 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                fontSize: isMobile ? '11px' : '12px',
                fontWeight: '500',
                borderRadius: '16px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {effect}
              </span>
            ))}
          </div>

          {/* Expandable Details */}
          {showDetails && (
            <div style={{
              marginTop: isMobile ? '12px' : '16px',
              padding: isMobile ? '12px' : '16px',
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '12px',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              animation: 'fadeIn 0.3s ease'
            }}>
              <p style={{ 
                margin: '0 0 12px 0', 
                fontSize: isMobile ? '13px' : '14px',
                lineHeight: '1.5',
                opacity: 0.9
              }}>
                {product.description}
              </p>
              <div style={{ fontSize: isMobile ? '11px' : '12px', opacity: 0.8 }}>
                <p style={{ margin: '0 0 4px 0' }}>
                  <strong>Flavors:</strong> {product.flavors.join(', ')}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Lab Info:</strong> {product.lab}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Swipe Indicators */}
       {isTop && (
        <>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-30deg)',
            padding: isMobile ? '12px 24px' : '16px 32px',
            background: 'rgba(239, 68, 68, 0.9)',
            color: 'white',
            fontSize: isMobile ? '18px' : '24px',
            fontWeight: '800',
            borderRadius: '12px',
            border: '3px solid #ef4444',
            opacity: (swipeDirection === 'left' || (isDragging && currentX - startX < -50)) ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            {isDragging && currentX - startX < -100 ? '1⭐' : 'PASS'}
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(30deg)',
            padding: isMobile ? '12px 24px' : '16px 32px',
            background: 'rgba(16, 185, 129, 0.9)',
            color: 'white',
            fontSize: isMobile ? '18px' : '24px',
            fontWeight: '800',
            borderRadius: '12px',
            border: '3px solid #10b981',
            opacity: (swipeDirection === 'right' || (isDragging && currentX - startX > 50)) ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            {isDragging && currentX - startX > 100 ? '5⭐' : 'LIKE'}
          </div>
        </>
      )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(80px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};